import { createTheme, type ThemeOptions } from "@mui/material/styles";

// Yoda-green palette + AI-native gradient accents.
const sharedTypography: ThemeOptions["typography"] = {
  fontFamily: [
    "Poppins",
    "system-ui",
    "-apple-system",
    "Segoe UI",
    "Helvetica",
    "Arial",
    "sans-serif",
  ].join(","),
  h1: { fontWeight: 700, letterSpacing: "-0.02em" },
  h2: { fontWeight: 700, letterSpacing: "-0.02em" },
  h3: { fontWeight: 700, letterSpacing: "-0.01em" },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
  button: { textTransform: "none", fontWeight: 600 },
};

const sharedShape: ThemeOptions["shape"] = { borderRadius: 12 };

// AI-native gradient sampled from the JobYoda owl logo.
// pale lime → sage → leaf → deep forest. Stays on-brand and high-contrast
// against a near-black text color (#0E2A10) which passes WCAG AA on every
// stop of this gradient. White text would fail AA on the lighter greens.
const AI_GRADIENT_LIGHT_GREEN =
  "linear-gradient(135deg, #D7E89B 0%, #A6D785 35%, #7BB661 70%, #4F8A3A 100%)";
const AI_GRADIENT_LIGHT_GREEN_HOVER =
  "linear-gradient(135deg, #E1EFAB 0%, #B5DF95 35%, #8AC472 70%, #5C9B45 100%)";
// Dark mode keeps the lighter side of the palette so the button glows on a
// dark background while still using only logo-green tones.
const AI_GRADIENT_DARK_GREEN =
  "linear-gradient(135deg, #C8E6A0 0%, #A6D785 40%, #7BB661 100%)";
const AI_GRADIENT_DARK_GREEN_HOVER =
  "linear-gradient(135deg, #D4ECB0 0%, #B5DF95 40%, #8AC472 100%)";

const AI_BUTTON_TEXT = "#0E2A10"; // deep forest from the logo outline

const aiButtonOverrides = (
  gradient: string,
  gradientHover: string,
  borderColor: string,
): ThemeOptions["components"] => ({
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ ownerState }) => {
        if (
          ownerState.variant !== "contained" ||
          (ownerState.color && ownerState.color !== "primary")
        ) {
          return {};
        }
        return {
          backgroundImage: gradient,
          backgroundColor: "transparent",
          color: AI_BUTTON_TEXT,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
          transition: "all 0.25s ease",
          "&:hover": {
            backgroundImage: gradientHover,
            backgroundColor: "transparent",
            boxShadow: "none",
            transform: "translateY(-1px)",
          },
          "&.Mui-disabled": {
            backgroundImage: "none",
            color: "rgba(14,42,16,0.45)",
            border: "1px solid rgba(14,42,16,0.15)",
          },
        };
      },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: ({ ownerState }) => {
        if (ownerState.color !== "primary") return {};
        return {
          fill: "url(#jobyoda-icon-gradient)",
          color: "transparent",
        };
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#A6D785", contrastText: "#0E2A10" },
    secondary: { main: "#C8E6A0", contrastText: "#1B3A14" },
    background: { default: "#0E1410", paper: "#161D17" },
    error: { main: "#FFB4AB", contrastText: "#1A0E0C" },
    warning: { main: "#FFB870", contrastText: "#1F1300" },
    success: { main: "#7DD89C", contrastText: "#0E2614" },
    info: { main: "#A6D785", contrastText: "#0E2A10" },
    divider: "rgba(255,255,255,0.08)",
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(14,20,16,0.7)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: "1px solid rgba(255,255,255,0.06)" } },
    },
    ...aiButtonOverrides(AI_GRADIENT_DARK_GREEN, AI_GRADIENT_DARK_GREEN_HOVER, "rgba(79,138,58,0.6)"),
  },
});

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5A8C3E", contrastText: "#FFFFFF" },
    secondary: { main: "#2E4D2A", contrastText: "#FFFFFF" },
    background: { default: "#F4F7F0", paper: "#FFFFFF" },
    info: { main: "#1F6FB2", contrastText: "#FFFFFF" },
    warning: { main: "#B86E00", contrastText: "#FFFFFF" },
    success: { main: "#2F7D4F", contrastText: "#FFFFFF" },
    error: { main: "#B3261E", contrastText: "#FFFFFF" },
    divider: "rgba(0,0,0,0.08)",
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(255,255,255,0.8)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: "1px solid rgba(0,0,0,0.08)" } },
    },
    ...aiButtonOverrides(AI_GRADIENT_LIGHT_GREEN, AI_GRADIENT_LIGHT_GREEN_HOVER, "#4F8A3A"),
  },
});
